import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { timeApi } from '../api/time.api';
import { useTimer } from '@/app/store/timer.store';

export const timerKeys = { snapshot: ['timer'] as const };

/** Keeps the timer store in sync with the server every 30 s and exposes start/stop. */
export function useTimerControls() {
  const client = useQueryClient();
  const hydrate = useTimer((s) => s.hydrate);

  const query = useQuery({ queryKey: timerKeys.snapshot, queryFn: timeApi.timer, refetchInterval: 30_000 });

  useEffect(() => {
    if (query.isSuccess) hydrate(query.data);
  }, [query.isSuccess, query.data, hydrate]);

  const invalidate = (taskId?: string) => {
    void client.invalidateQueries({ queryKey: timerKeys.snapshot });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
    void client.invalidateQueries({ queryKey: ['tasks'] });
    if (taskId) void client.invalidateQueries({ queryKey: ['task', taskId] });
  };

  const start = useMutation({
    mutationFn: (taskId: string) => timeApi.start(taskId),
    onSuccess: (snapshot) => {
      hydrate(snapshot);
      invalidate(snapshot.taskId);
    }
  });

  const stop = useMutation({
    mutationFn: () => timeApi.stop(),
    onSuccess: () => {
      const previous = useTimer.getState().snapshot?.taskId;
      hydrate(null);
      invalidate(previous);
    }
  });

  return {
    start: (taskId: string) => start.mutateAsync(taskId),
    stop: () => stop.mutateAsync(),
    starting: start.isPending,
    stopping: stop.isPending
  };
}

/** Drives the 1-second tick. Mount once inside the authenticated shell. */
export function useTimerTick() {
  const tick = useTimer((s) => s.tick);
  useEffect(() => {
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [tick]);
}
